import { SearchConversationEnriched } from 'lib-search-types';
import { ConversationStatus } from 'lib-shared-types-api';
import { useState } from 'react';
import { useEffectOnce } from 'react-use';

import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/**
 * Subscribes to conversations linking and status related updates and stores
 * state changes in-memory.
 */
export const useConversationsStateEffect = ({
  requesterId,
  conversations,
}: {
  requesterId: string | undefined;
  conversations: readonly SearchConversationEnriched[];
}): {
  currentlyLinkedCsids: Record<number, number>;
  currentlyClosedCsids: Record<number, ConversationStatus>;
} => {
  const dispatch = useAppDispatch();

  const [currentlyLinkedCsids, setCurrentlyLinkedCsids] = useState<
    Record<number, number>
  >({});
  const [currentlyClosedCsids, setCurrentlyClosedCsids] = useState<
    Record<number, ConversationStatus>
  >({});

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe(
      'bulkActionFinished',
      ({ arg, notification_type, type }) => {
        /**
         * Reflect bulk action link changes in the UI using in memory state.
         * Rely on in-memory state due to:
         *
         * - Endpoint being paginated
         * - Search cluster takes time to sync changes and might take seconds for
         *   the link changes to reflect in data
         */

        if (type === 'link' && notification_type === 'BulkActionSucceeded') {
          arg.csids.forEach((csid) => {
            // Add linked conversations to a linked section in the main conversation view.
            dispatch(conversationActions.conversationLinkChild({ csid }));
          });

          setCurrentlyLinkedCsids((linked) => {
            const currentLinked = {};
            // Store parent csid in case if conversation is changed during the bulk action.
            arg.csids.forEach((csid) => {
              currentLinked[csid] = linked[csid] ?? arg.parent_csid;
            });

            return { ...linked, ...currentLinked };
          });
        }
      },
    );

    return unsubscribe;
  });

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe('conversationUnlinked', ({ csid }) => {
      /**
       * Linked conversation id can not be -1. Hence treat it as the special
       * case of unlinked conversations. To disregard the stale linked_to value
       * on a conversation object
       */
      setCurrentlyLinkedCsids((linked) => ({ ...linked, [csid]: -1 }));
    });

    return unsubscribe;
  });

  /**
   * Reflect conversation status changes in the UI using in memory state.
   * Reasons for relying on in-memory state are the same as with bulk action
   * link.
   */
  useConversationEventsEffect(({ params: { conversation, requester } }) => {
    if (requester.id !== requesterId) {
      return;
    }

    const current = conversations.find(({ id }) => conversation.id === id);
    if (!current) {
      return;
    }

    setCurrentlyClosedCsids((closed) => {
      const currentStatus = closed[conversation.id] ?? current.status;

      return currentStatus !== conversation.status
        ? { ...closed, [conversation.id]: conversation.status }
        : closed;
    });
  });

  return { currentlyLinkedCsids, currentlyClosedCsids };
};
